/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import ExcelJS from 'exceljs';
import { SafetyTalk, SafetyTalkAttendee, SafetyTalkStatus } from '../types.ts';

export interface ParsedSafetyTalksResult {
  validTalks: SafetyTalk[];
  invalidRows: { rowNumber: number; reason: string; rawData: Record<string, any> }[];
  totalRows: number;
}

const STPS_SAMPLE_TOPICS = [
  { topic: 'Uso y Conservación del Equipo de Protección Personal (EPP)', standard: 'NOM-017-STPS-2008 / EPP', area: 'Planta Operativa', facilitator: 'Alondra Tovar', role: 'Supervisor HSEQ', objective: 'Asegurar la correcta colocación, ajuste y cuidado de cascos, lentes y calzado dieléctrico.' },
  { topic: 'Prevención de Caídas y Trabajos Seguros en Alturas', standard: 'NOM-009-STPS-2011 / Alturas', area: 'Mantenimiento Mecánico', facilitator: 'Carlos Mendoza', role: 'Líder de Seguridad', objective: 'Revisión obligatoria del arnés de cuerpo entero, línea de vida y puntos de anclaje certificados.' },
  { topic: 'Identificación de Peligros por Sustancias Químicas (HDS / SGA)', standard: 'NOM-018-STPS-2015 / SGA', area: 'Decapado y Químicos', facilitator: 'Alondra Tovar', role: 'Supervisor HSEQ', objective: 'Lectura de rombos y pictogramas SGA en envases secundarios de desengrasantes y ácidos.' },
  { topic: 'Seguridad en Maquinaria y Dispositivos de Bloqueo / Etiquetado (LOTO)', standard: 'NOM-004-STPS-1999 / LOTO', area: 'Hornos y Piro-oxidación', facilitator: 'Roberto Silva', role: 'Jefe de Mantenimiento', objective: 'Protocolo de cero energía peligrosa con candado personal antes de intervenir equipos.' },
  { topic: 'Manejo Seguro de Sustancias Inflamables y Solventes', standard: 'NOM-005-STPS-1998 / Inflamables', area: 'Pintura y Recubrimientos', facilitator: 'Alondra Tovar', role: 'Supervisor HSEQ', objective: 'Puesta a tierra de tambos y prevención de chispas en trasvase de solventes.' },
  { topic: 'Prevención de Incendios, Extintores y Rutas de Evacuación', standard: 'NOM-002-STPS-2010 / Fuego', area: 'Planta General', facilitator: 'Diana Ramos', role: 'Brigadista HSEQ', objective: 'Ubicación de extintores PQS/CO2, pasillos libres y punto de reunión asignado.' },
  { topic: 'Ergonomía e Higiene Postural en Manejo Manual de Cargas', standard: 'NOM-036-1-STPS-2018 / Ergonomía', area: 'Almacén y Embarques', facilitator: 'Carlos Mendoza', role: 'Líder de Seguridad', objective: 'Técnica de levantamiento con flexión de piernas sin doblar la espalda (límite 25 kg).' },
  { topic: 'Señales, Avisos de Seguridad y Código de Colores', standard: 'NOM-026-STPS-2008 / Señalética', area: 'Patios y Tránsito', facilitator: 'Alondra Tovar', role: 'Supervisor HSEQ', objective: 'Identificación de zonas peatonales, restricción vehicular y advertencias visuales.' },
  { topic: 'Trabajos en Espacios Confinados y Pruebas de Atmósfera', standard: 'NOM-033-STPS-2015 / Confinados', area: 'Mantenimiento Mayor', facilitator: 'Roberto Silva', role: 'Jefe de Mantenimiento', objective: 'Medición obligatoria de oxígeno/gases y vigía permanente antes de ingresar a fosas.' },
  { topic: 'Manejo y Almacenamiento Seguro con Montacargas', standard: 'NOM-006-STPS-2014 / Montacargas', area: 'Logística y Patios', facilitator: 'Carlos Mendoza', role: 'Líder de Seguridad', objective: 'Velocidad máxima de 10 km/h, uso de claxon en esquinas y checklist diario del equipo.' },
  { topic: 'Factores de Riesgo Psicosocial y Entorno Organizacional Favorable', standard: 'NOM-035-STPS-2018 / Psicosocial', area: 'Administración y Líneas', facilitator: 'Diana Ramos', role: 'Brigadista HSEQ', objective: 'Fomento del compañerismo, comunicación asertiva y reporte de jornadas excesivas.' },
  { topic: 'Metodología 5S: Orden y Limpieza como Prevención de Accidentes', standard: 'ISO 45001 / Buenas Prácticas', area: 'Piso de Producción', facilitator: 'Alondra Tovar', role: 'Supervisor HSEQ', objective: 'Eliminar obstáculos en pasillos y mantener herramientas en sus tableros asignados.' }
];

/**
 * Generates and triggers download of an executive, beautifully styled Excel template (.xlsx)
 * preconfigured for 5-minute safety talks bulk planning.
 */
export async function generateSafetyTalksTemplateExcel(
  year: number = new Date().getFullYear(),
  monthIndex: number = new Date().getMonth(),
  costCenterName: string = 'Sede Bajío - Salamanca'
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'GRUPO ISN - PORTAL HSEQ';
  workbook.created = new Date();

  const monthStr = String(monthIndex + 1).padStart(2, '0');
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  const monthName = monthNames[monthIndex] || 'Mes';

  // -------------------------------------------------------------
  // HOJA 1: PLANTILLA DE CARGA DE PLÁTICAS
  // -------------------------------------------------------------
  const ws = workbook.addWorksheet('Pláticas_5Min_Plantilla', {
    views: [{ showGridLines: true, state: 'frozen', xSplit: 0, ySplit: 5 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
  });

  // Encabezado Corporativo
  ws.mergeCells('A1:M1');
  const titleCell = ws.getCell('A1');
  titleCell.value = `GRUPO ISN - SISTEMA HSEQ | PLANTILLA DE CARGA MASIVA DE PLÁTICAS DE 5 MINUTOS`;
  titleCell.font = { name: 'Segoe UI', size: 13, bold: true, color: { argb: 'FFFFFFFF' } };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF064E3B' } }; // Deep emerald
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(1).height = 28;

  ws.mergeCells('A2:M2');
  const subCell = ws.getCell('A2');
  subCell.value = `Sede / Centro de Costos: ${costCenterName} | Periodo: ${monthName} ${year} | Cumplimiento STPS NOM-019 / ISO 45001`;
  subCell.font = { name: 'Segoe UI', size: 10, bold: true, color: { argb: 'FFD1FAE5' } };
  subCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF047857' } }; // Emerald 700
  subCell.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(2).height = 20;

  ws.mergeCells('A3:M3');
  const noteCell = ws.getCell('A3');
  noteCell.value = `INSTRUCCIONES: Complete una fila por cada plática a calendarizar. Los campos con (*) son obligatorios. Formato de fecha: AAAA-MM-DD. Estatus válidos: programada, aplicada, no_realizada.`;
  noteCell.font = { name: 'Segoe UI', size: 9, italic: true, color: { argb: 'FF065F46' } };
  noteCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFECFDF5' } }; // Light mint
  noteCell.alignment = { horizontal: 'left', vertical: 'middle' };
  ws.getRow(3).height = 18;

  ws.getRow(4).height = 6; // Spacing row

  // Encabezados de Columna (Fila 5)
  const columnsConfig = [
    { key: 'date', label: 'Fecha (*)\nAAAA-MM-DD', width: 15 },
    { key: 'time', label: 'Hora\nHH:mm', width: 10 },
    { key: 'shift', label: 'Turno\n(Matutino/Vesp)', width: 14 },
    { key: 'topic', label: 'Tema de la Plática (*)\n(Nombre descriptivo)', width: 36 },
    { key: 'standardCode', label: 'Norma STPS / Ref.\n(Ej: NOM-017-STPS)', width: 24 },
    { key: 'area', label: 'Área / Línea\n(Ej: Decapado)', width: 22 },
    { key: 'facilitator', label: 'Facilitador / Exponente (*)\n(Nombre completo)', width: 24 },
    { key: 'facilitatorRole', label: 'Puesto del Facilitador\n(Ej: Supervisor HSEQ)', width: 22 },
    { key: 'objective', label: 'Objetivo de la Plática\n(Propósito preventivo)', width: 38 },
    { key: 'attendeesCount', label: 'No. Asistentes\n(Estimado)', width: 14 },
    { key: 'status', label: 'Estatus (*)\n(programada/aplicada)', width: 16 },
    { key: 'durationMinutes', label: 'Duración\n(Minutos)', width: 12 },
    { key: 'observations', label: 'Observaciones / Puntos Clave\n(Notas adicionales)', width: 32 }
  ];

  const headerRow = ws.getRow(5);
  headerRow.height = 32;

  columnsConfig.forEach((col, idx) => {
    const cell = headerRow.getCell(idx + 1);
    cell.value = col.label;
    cell.font = { name: 'Segoe UI', size: 9, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F2937' } }; // Dark slate
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'medium', color: { argb: 'FF065F46' } },
      left: { style: 'thin', color: { argb: 'FF374151' } },
      bottom: { style: 'medium', color: { argb: 'FF065F46' } },
      right: { style: 'thin', color: { argb: 'FF374151' } }
    };
    ws.getColumn(idx + 1).width = col.width;
  });

  // Rellenar con filas de ejemplo representativas para el mes seleccionado
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const sampleRowsCount = Math.min(12, daysInMonth);

  for (let i = 0; i < sampleRowsCount; i++) {
    const dayNum = i + 1;
    const dayPadded = String(dayNum).padStart(2, '0');
    const dateStr = `${year}-${monthStr}-${dayPadded}`;
    const sampleTopic = STPS_SAMPLE_TOPICS[i % STPS_SAMPLE_TOPICS.length];
    
    // Status: past days marked as 'aplicada', future days as 'programada'
    const today = new Date();
    const isPastOrToday = new Date(year, monthIndex, dayNum) <= today;
    const statusVal: SafetyTalkStatus = isPastOrToday ? 'aplicada' : 'programada';

    const rowData = [
      dateStr,
      '07:30',
      i % 3 === 0 ? 'Matutino' : i % 3 === 1 ? 'Vespertino' : 'Mixto',
      sampleTopic.topic,
      sampleTopic.standard,
      sampleTopic.area,
      sampleTopic.facilitator,
      sampleTopic.role,
      sampleTopic.objective,
      12 + (i % 8),
      statusVal,
      10,
      `Verificación de firmas de asistencia y evidencias de la actividad #${i + 1}.`
    ];

    const dataRow = ws.addRow(rowData);
    dataRow.height = 22;

    const isEven = i % 2 === 0;
    dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = { name: 'Segoe UI', size: 9, color: { argb: 'FF1F2937' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: isEven ? 'FFFFFFFF' : 'FFF9FAFB' }
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        right: { style: 'thin', color: { argb: 'FFE5E7EB' } }
      };

      // Alignment logic
      if (colNumber === 1 || colNumber === 2 || colNumber === 3 || colNumber === 11) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      } else if (colNumber === 10 || colNumber === 12) {
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
      } else {
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
      }

      // Highlight status cell
      if (colNumber === 11) {
        if (cell.value === 'aplicada') {
          cell.font = { name: 'Segoe UI', size: 9, bold: true, color: { argb: 'FF065F46' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } };
        } else if (cell.value === 'programada') {
          cell.font = { name: 'Segoe UI', size: 9, bold: true, color: { argb: 'FF1E40AF' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } };
        }
      }
    });
  }

  // -------------------------------------------------------------
  // HOJA 2: CATÁLOGO DE NORMAS STPS Y TEMAS SUGERIDOS
  // -------------------------------------------------------------
  const wsCat = workbook.addWorksheet('Catálogo_Normas_STPS', {
    views: [{ showGridLines: true }]
  });

  wsCat.mergeCells('A1:D1');
  const catTitle = wsCat.getCell('A1');
  catTitle.value = 'CATÁLOGO OFICIAL DE TEMAS SUGERIDOS Y NORMAS STPS';
  catTitle.font = { name: 'Segoe UI', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
  catTitle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } };
  catTitle.alignment = { horizontal: 'center', vertical: 'middle' };
  wsCat.getRow(1).height = 24;

  const catHeaders = ['Norma STPS', 'Tema de Seguridad Recomendado', 'Área de Enfoque Principal', 'Objetivo / Alcance Clave'];
  const catHeaderRow = wsCat.addRow(catHeaders);
  catHeaderRow.height = 24;
  catHeaderRow.eachCell((cell) => {
    cell.font = { name: 'Segoe UI', size: 9, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  });

  STPS_SAMPLE_TOPICS.forEach((item) => {
    const row = wsCat.addRow([item.standard, item.topic, item.area, item.objective]);
    row.height = 20;
    row.eachCell((cell) => {
      cell.font = { name: 'Segoe UI', size: 9 };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
        bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
        left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
        right: { style: 'thin', color: { argb: 'FFE2E8F0' } }
      };
    });
  });

  wsCat.getColumn(1).width = 28;
  wsCat.getColumn(2).width = 45;
  wsCat.getColumn(3).width = 25;
  wsCat.getColumn(4).width = 50;

  // -------------------------------------------------------------
  // HOJA 3: INSTRUCCIONES DE LLENADO Y VALIDACIÓN
  // -------------------------------------------------------------
  const wsInst = workbook.addWorksheet('Instrucciones_Llenado');
  wsInst.mergeCells('A1:C1');
  const instTitle = wsInst.getCell('A1');
  instTitle.value = 'GUÍA RÁPIDA DE LLENADO Y CARGA MASIVA';
  instTitle.font = { name: 'Segoe UI', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
  instTitle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF065F46' } };
  instTitle.alignment = { horizontal: 'center', vertical: 'middle' };
  wsInst.getRow(1).height = 24;

  const instructions = [
    ['Paso 1', 'Descargar Plantilla', 'Utilice este archivo Excel como base oficial para planificar o registrar las pláticas de 5 minutos.'],
    ['Paso 2', 'Llenar Filas Requeridas', 'En la primera pestaña ("Pláticas_5Min_Plantilla"), agregue o edite las filas. La columna "Fecha" (AAAA-MM-DD) y "Tema de la Plática" son obligatorias.'],
    ['Paso 3', 'Estatus Válidos', 'Utilice: "programada" (para pláticas futuras), "aplicada" (para pláticas ya impartidas), o "no_realizada".'],
    ['Paso 4', 'Asistencia', 'Indique el número estimado o real de asistentes. El sistema generará automáticamente la lista nominal y hoja de firmas.'],
    ['Paso 5', 'Subir Archivo al Portal', 'Regrese al módulo de Pláticas de 5 Minutos, haga clic en "Carga Masiva" y arrastre o seleccione el archivo guardado.'],
    ['Paso 6', 'Evidencias Fotográficas', 'Una vez importadas, podrá abrir cualquier plática impartida para adjuntar hasta 7 fotos de evidencia en alta resolución y generar su acta PDF individual.']
  ];

  instructions.forEach((inst) => {
    const r = wsInst.addRow(inst);
    r.height = 24;
    r.getCell(1).font = { name: 'Segoe UI', size: 9, bold: true, color: { argb: 'FF065F46' } };
    r.getCell(2).font = { name: 'Segoe UI', size: 9, bold: true };
    r.getCell(3).font = { name: 'Segoe UI', size: 9 };
    r.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        right: { style: 'thin', color: { argb: 'FFE5E7EB' } }
      };
      cell.alignment = { vertical: 'middle' };
    });
  });

  wsInst.getColumn(1).width = 14;
  wsInst.getColumn(2).width = 26;
  wsInst.getColumn(3).width = 65;

  // Generar Buffer y Descargar
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `Plantilla_Platicas_5Min_${monthName}_${year}.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Generates and triggers download of a quick CSV template.
 */
export function generateSafetyTalksTemplateCSV(
  year: number = new Date().getFullYear(),
  monthIndex: number = new Date().getMonth()
): void {
  const monthStr = String(monthIndex + 1).padStart(2, '0');
  const headers = [
    'Fecha (AAAA-MM-DD)',
    'Hora',
    'Turno',
    'Tema de la Platica',
    'Norma STPS / Ref',
    'Area',
    'Facilitador',
    'Puesto Facilitador',
    'Objetivo',
    'No Asistentes',
    'Estatus',
    'Duracion Minutos',
    'Observaciones'
  ];

  const sampleRows = STPS_SAMPLE_TOPICS.slice(0, 5).map((item, idx) => {
    const day = String(idx + 1).padStart(2, '0');
    return [
      `"${year}-${monthStr}-${day}"`,
      `"07:30"`,
      `"Matutino"`,
      `"${item.topic}"`,
      `"${item.standard}"`,
      `"${item.area}"`,
      `"${item.facilitator}"`,
      `"${item.role}"`,
      `"${item.objective}"`,
      `15`,
      `"programada"`,
      `10`,
      `"Carga inicial mediante plantilla CSV"`
    ].join(',');
  });

  const csvContent = '\uFEFF' + [headers.join(','), ...sampleRows].join('\r\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Plantilla_Platicas_5Min_${year}_${monthStr}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Creates default list of attendees for an imported talk.
 */
function createDefaultAttendeesList(count: number, baseDate?: string): SafetyTalkAttendee[] {
  const targetCount = Math.max(count || 12, 12);
  return Array.from({ length: targetCount }, (_, i) => ({
    index: i + 1,
    name: '',
    accepted: 'SI' as const,
    signature: '',
    date: baseDate || ''
  }));
}

/**
 * Helper to normalize date string to YYYY-MM-DD
 */
function normalizeDateValue(rawVal: any): string | null {
  if (!rawVal) return null;

  // Handle Date object
  if (rawVal instanceof Date && !isNaN(rawVal.getTime())) {
    const y = rawVal.getFullYear();
    const m = String(rawVal.getMonth() + 1).padStart(2, '0');
    const d = String(rawVal.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // Handle Excel Serial number (e.g. 45500)
  if (typeof rawVal === 'number' && rawVal > 30000 && rawVal < 60000) {
    const excelEpoch = new Date(1899, 11, 30);
    const dateObj = new Date(excelEpoch.getTime() + rawVal * 86400000);
    if (!isNaN(dateObj.getTime())) {
      const y = dateObj.getFullYear();
      const m = String(dateObj.getMonth() + 1).padStart(2, '0');
      const d = String(dateObj.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }

  const str = String(rawVal).trim();

  // Match YYYY-MM-DD or YYYY/MM/DD
  const isoMatch = str.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (isoMatch) {
    const y = isoMatch[1];
    const m = isoMatch[2].padStart(2, '0');
    const d = isoMatch[3].padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // Match DD/MM/YYYY or DD-MM-YYYY
  const dmyMatch = str.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
  if (dmyMatch) {
    const d = dmyMatch[1].padStart(2, '0');
    const m = dmyMatch[2].padStart(2, '0');
    const y = dmyMatch[3];
    return `${y}-${m}-${d}`;
  }

  return null;
}

/**
 * Normalizes status value to 'programada' | 'aplicada' | 'no_realizada'
 */
function normalizeStatusValue(rawStatus: any, dateStr?: string): SafetyTalkStatus {
  if (!rawStatus) {
    if (dateStr) {
      const isPast = new Date(dateStr) <= new Date();
      return isPast ? 'aplicada' : 'programada';
    }
    return 'programada';
  }

  const s = String(rawStatus).toLowerCase().trim();
  if (s.includes('aplicad') || s.includes('complet') || s.includes('impartid') || s.includes('realizad') || s === 'si') {
    return 'aplicada';
  }
  if (s.includes('no') || s.includes('cancel') || s.includes('pend') || s.includes('venc')) {
    return 'no_realizada';
  }
  return 'programada';
}

/**
 * Parses an uploaded Excel (.xlsx, .xls) or CSV file containing 5-minute safety talks.
 */
export async function parseSafetyTalksFile(
  file: File,
  defaultCostCenterId: string = 'CC-MAZDA-BAJIO',
  defaultCostCenterName: string = 'Sede Bajío - Salamanca'
): Promise<ParsedSafetyTalksResult> {
  const fileName = file.name.toLowerCase();
  const validTalks: SafetyTalk[] = [];
  const invalidRows: { rowNumber: number; reason: string; rawData: Record<string, any> }[] = [];

  const buffer = await file.arrayBuffer();
  const workbook = new ExcelJS.Workbook();

  if (fileName.endsWith('.csv')) {
    // For CSV files, read via csv text parser
    const text = new TextDecoder('utf-8').decode(buffer);
    const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
    if (lines.length <= 1) {
      return { validTalks: [], invalidRows: [{ rowNumber: 1, reason: 'El archivo CSV está vacío o sin datos.', rawData: {} }], totalRows: 0 };
    }

    // Split CSV line handling quotes
    const parseCsvLine = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if ((char === ',' || char === ';') && !inQuotes) {
          result.push(current.trim().replace(/^"|"$/g, ''));
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim().replace(/^"|"$/g, ''));
      return result;
    };

    const headerCols = parseCsvLine(lines[0]).map(h => h.toLowerCase().trim());
    
    // Map column index to field
    const getColIndex = (candidates: string[]) => {
      return headerCols.findIndex(h => candidates.some(c => h.includes(c)));
    };

    const colDate = getColIndex(['fecha', 'date']);
    const colTopic = getColIndex(['tema', 'topic', 'platica', 'plática', 'titulo', 'nombre']);
    const colStandard = getColIndex(['norma', 'standard', 'stps', 'ref', 'codigo']);
    const colArea = getColIndex(['area', 'área', 'depto', 'departamento', 'linea', 'planta']);
    const colFacilitator = getColIndex(['facilitador', 'exponente', 'instructor', 'impartio', 'responsable', 'nombre']);
    const colRole = getColIndex(['puesto', 'cargo', 'rol', 'role']);
    const colObj = getColIndex(['objetivo', 'objective', 'proposito']);
    const colCount = getColIndex(['asistentes', 'asistencia', 'attendees', 'personas', 'cantidad', 'no']);
    const colStatus = getColIndex(['estatus', 'status', 'estado']);
    const colTime = getColIndex(['hora', 'time', 'horario']);
    const colShift = getColIndex(['turno', 'shift']);
    const colDuration = getColIndex(['duracion', 'duración', 'minutos', 'duration']);
    const colObs = getColIndex(['observaciones', 'notas', 'comentarios', 'puntos']);

    for (let idx = 1; idx < lines.length; idx++) {
      const line = lines[idx];
      if (!line.trim()) continue;
      const values = parseCsvLine(line);
      const rawData = { lineContent: line, values };

      const rawDate = colDate >= 0 ? values[colDate] : values[0];
      const parsedDate = normalizeDateValue(rawDate);
      const rawTopic = colTopic >= 0 ? values[colTopic] : values[3] || values[1];

      if (!parsedDate) {
        invalidRows.push({ rowNumber: idx + 1, reason: `Fecha inválida o vacía ("${rawDate || ''}")`, rawData });
        continue;
      }

      if (!rawTopic || rawTopic.trim().length === 0) {
        invalidRows.push({ rowNumber: idx + 1, reason: 'El tema de la plática está vacío.', rawData });
        continue;
      }

      const dayNum = parseInt(parsedDate.split('-')[2], 10) || 1;
      const attendeesCount = colCount >= 0 && !isNaN(Number(values[colCount])) ? Number(values[colCount]) : 12;
      const status = normalizeStatusValue(colStatus >= 0 ? values[colStatus] : undefined, parsedDate);

      const talk: SafetyTalk = {
        id: `PL-IMP-${Date.now().toString().slice(-4)}-${idx}`,
        costCenterId: defaultCostCenterId,
        costCenterName: defaultCostCenterName,
        businessUnit: defaultCostCenterName.includes('-') ? defaultCostCenterName.split('-')[0].trim() : 'Bajío',
        project: defaultCostCenterName.includes('-') ? defaultCostCenterName.split('-')[0].trim() : 'Bajío',
        date: parsedDate,
        day: dayNum,
        time: (colTime >= 0 && values[colTime]) ? values[colTime].trim() : '07:30',
        shift: (colShift >= 0 && values[colShift]) ? values[colShift].trim() : 'Matutino',
        topic: rawTopic.trim(),
        standardCode: (colStandard >= 0 && values[colStandard]) ? values[colStandard].trim() : 'NOM-017-STPS-2008 / EPP',
        area: (colArea >= 0 && values[colArea]) ? values[colArea].trim() : 'Planta Operativa',
        facilitator: (colFacilitator >= 0 && values[colFacilitator]) ? values[colFacilitator].trim() : 'Alondra Tovar',
        facilitatorRole: (colRole >= 0 && values[colRole]) ? values[colRole].trim() : 'Supervisor de Seguridad HSEQ',
        objective: (colObj >= 0 && values[colObj]) ? values[colObj].trim() : 'Capacitación y prevención continua según estándares STPS.',
        attendeesCount,
        attendees: createDefaultAttendeesList(attendeesCount, parsedDate),
        status,
        durationMinutes: (colDuration >= 0 && !isNaN(Number(values[colDuration]))) ? Number(values[colDuration]) : 10,
        description: `Plática de 5 minutos registrada mediante carga masiva. Tema: ${rawTopic.trim()}.`,
        observations: (colObs >= 0 && values[colObs]) ? values[colObs].trim() : 'Importado vía plantilla masiva.',
        photos: [],
        registeredBy: 'Importación Masiva Excel/CSV',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      validTalks.push(talk);
    }

    return { validTalks, invalidRows, totalRows: validTalks.length + invalidRows.length };
  }

  // Handle Excel (.xlsx, .xls)
  await workbook.xlsx.load(buffer);

  // Find the right worksheet (first one or named containing platicas / datos)
  let worksheet = workbook.worksheets.find(ws => 
    ws.name.toLowerCase().includes('platica') || 
    ws.name.toLowerCase().includes('5min') || 
    ws.name.toLowerCase().includes('plantilla')
  ) || workbook.worksheets[0];

  if (!worksheet) {
    return { validTalks: [], invalidRows: [{ rowNumber: 1, reason: 'No se encontraron hojas en el archivo Excel.', rawData: {} }], totalRows: 0 };
  }

  // Find header row (usually row 5, or find row with 'fecha' / 'date' / 'tema')
  let headerRowIndex = 5;
  for (let r = 1; r <= Math.min(10, worksheet.rowCount); r++) {
    const row = worksheet.getRow(r);
    const rowValues = (row.values as any[]) || [];
    const rowText = rowValues.map(v => (v ? String(v).toLowerCase() : '')).join(' ');
    if (rowText.includes('fecha') || rowText.includes('date') || rowText.includes('tema')) {
      headerRowIndex = r;
      break;
    }
  }

  const headerRow = worksheet.getRow(headerRowIndex);
  const colMap: Record<string, number> = {};

  headerRow.eachCell((cell, colNumber) => {
    const val = String(cell.value || '').toLowerCase().trim();
    if (val.includes('fecha') || val.includes('date')) colMap['date'] = colNumber;
    else if (val.includes('hora') || val.includes('time')) colMap['time'] = colNumber;
    else if (val.includes('turno') || val.includes('shift')) colMap['shift'] = colNumber;
    else if (val.includes('tema') || val.includes('topic') || val.includes('platica') || val.includes('plática')) colMap['topic'] = colNumber;
    else if (val.includes('norma') || val.includes('standard') || val.includes('stps') || val.includes('ref')) colMap['standard'] = colNumber;
    else if (val.includes('area') || val.includes('área') || val.includes('depto') || val.includes('linea')) colMap['area'] = colNumber;
    else if (val.includes('facilitador') || val.includes('exponente') || val.includes('instructor')) colMap['facilitator'] = colNumber;
    else if (val.includes('puesto') || val.includes('cargo') || val.includes('rol')) colMap['role'] = colNumber;
    else if (val.includes('objetivo') || val.includes('objective')) colMap['objective'] = colNumber;
    else if (val.includes('asistente') || val.includes('asistencia') || val.includes('attendee') || val.includes('cantidad')) colMap['count'] = colNumber;
    else if (val.includes('estatus') || val.includes('status') || val.includes('estado')) colMap['status'] = colNumber;
    else if (val.includes('duracion') || val.includes('duración') || val.includes('minuto')) colMap['duration'] = colNumber;
    else if (val.includes('observaci') || val.includes('nota') || val.includes('comentario')) colMap['obs'] = colNumber;
  });

  // Fallbacks if header matching missed
  const colDate = colMap['date'] || 1;
  const colTime = colMap['time'] || 2;
  const colShift = colMap['shift'] || 3;
  const colTopic = colMap['topic'] || 4;
  const colStandard = colMap['standard'] || 5;
  const colArea = colMap['area'] || 6;
  const colFacilitator = colMap['facilitator'] || 7;
  const colRole = colMap['role'] || 8;
  const colObj = colMap['objective'] || 9;
  const colCount = colMap['count'] || 10;
  const colStatus = colMap['status'] || 11;
  const colDuration = colMap['duration'] || 12;
  const colObs = colMap['obs'] || 13;

  for (let r = headerRowIndex + 1; r <= worksheet.rowCount; r++) {
    const row = worksheet.getRow(r);
    const rawDateVal = row.getCell(colDate).value;
    const rawTopicVal = row.getCell(colTopic).value;

    // Skip fully empty trailing rows
    if (!rawDateVal && !rawTopicVal) continue;

    // Helper to extract cell text
    const getCellText = (cellVal: any): string => {
      if (cellVal === null || cellVal === undefined) return '';
      if (typeof cellVal === 'object') {
        if (cellVal.richText) {
          return cellVal.richText.map((rt: any) => rt.text).join('');
        }
        if (cellVal.text) return String(cellVal.text);
        if (cellVal.result !== undefined) return String(cellVal.result);
      }
      return String(cellVal).trim();
    };

    const parsedDate = normalizeDateValue(rawDateVal);
    const topicText = getCellText(rawTopicVal);

    const rawData = {
      rowNumber: r,
      date: rawDateVal,
      topic: rawTopicVal,
      facilitator: row.getCell(colFacilitator).value,
      status: row.getCell(colStatus).value
    };

    if (!parsedDate) {
      invalidRows.push({
        rowNumber: r,
        reason: `Fecha inválida o no reconocida ("${getCellText(rawDateVal)}")`,
        rawData
      });
      continue;
    }

    if (!topicText || topicText.trim().length === 0) {
      invalidRows.push({
        rowNumber: r,
        reason: 'El tema de la plática está vacío o no fue especificado.',
        rawData
      });
      continue;
    }

    const dayNum = parseInt(parsedDate.split('-')[2], 10) || 1;
    const rawCount = row.getCell(colCount).value;
    const attendeesCount = typeof rawCount === 'number' ? rawCount : !isNaN(Number(getCellText(rawCount))) && getCellText(rawCount) !== '' ? Number(getCellText(rawCount)) : 12;
    const rawStatus = row.getCell(colStatus).value;
    const status = normalizeStatusValue(getCellText(rawStatus), parsedDate);

    const rawDuration = row.getCell(colDuration).value;
    const durationMinutes = typeof rawDuration === 'number' ? rawDuration : !isNaN(Number(getCellText(rawDuration))) && getCellText(rawDuration) !== '' ? Number(getCellText(rawDuration)) : 10;

    const talk: SafetyTalk = {
      id: `PL-IMP-${Date.now().toString().slice(-4)}-${r}`,
      costCenterId: defaultCostCenterId,
      costCenterName: defaultCostCenterName,
      businessUnit: defaultCostCenterName.includes('-') ? defaultCostCenterName.split('-')[0].trim() : 'Bajío',
      project: defaultCostCenterName.includes('-') ? defaultCostCenterName.split('-')[0].trim() : 'Bajío',
      date: parsedDate,
      day: dayNum,
      time: getCellText(row.getCell(colTime).value) || '07:30',
      shift: getCellText(row.getCell(colShift).value) || 'Matutino',
      topic: topicText.trim(),
      standardCode: getCellText(row.getCell(colStandard).value) || 'NOM-017-STPS-2008 / EPP',
      area: getCellText(row.getCell(colArea).value) || 'Planta Operativa',
      facilitator: getCellText(row.getCell(colFacilitator).value) || 'Alondra Tovar',
      facilitatorRole: getCellText(row.getCell(colRole).value) || 'Supervisor de Seguridad HSEQ',
      objective: getCellText(row.getCell(colObj).value) || 'Capacitación continua y cumplimiento de normas STPS.',
      attendeesCount,
      attendees: createDefaultAttendeesList(attendeesCount, parsedDate),
      status,
      durationMinutes,
      description: `Plática de 5 minutos calendarizada mediante carga masiva. Tema: ${topicText.trim()}.`,
      observations: getCellText(row.getCell(colObs).value) || 'Cargado vía Excel.',
      photos: [],
      registeredBy: 'Importación Masiva Excel',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    validTalks.push(talk);
  }

  return { validTalks, invalidRows, totalRows: validTalks.length + invalidRows.length };
}
